let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;
let sessionListener: ((token: string | null) => void) | null = null;
let mfaListener: ((code: MfaChallengeCode) => void) | null = null;

/** What a privileged route answers until the session has proved the authenticator code. */
export type MfaChallengeCode = "MFA_REQUIRED" | "MFA_ENROLLMENT_REQUIRED";

/** The staff second-factor gate listens here, so a verification that lapses mid-session reopens the prompt. */
export function setMfaChallengeListener(listener: (code: MfaChallengeCode) => void): () => void {
  mfaListener = listener;
  return () => { if (mfaListener === listener) mfaListener = null; };
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly correlationId?: string,
    readonly details?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// A validation failure carries the actionable text in `details` — the top-level message is
// only "Revisa los datos ingresados.". Both shapes the API sends are unwrapped here: the
// hand-built `{ field: string[] }` and zod's `flatten()` output.
function collectDetailMessages(details: unknown): string[] {
  if (!details || typeof details !== "object") return [];
  const source = details as Record<string, unknown>;
  const groups: unknown[] = [];
  if (Array.isArray(source.formErrors)) groups.push(source.formErrors);
  if (source.fieldErrors && typeof source.fieldErrors === "object") {
    groups.push(...Object.values(source.fieldErrors as Record<string, unknown>));
  }
  if (!groups.length) groups.push(...Object.values(source));
  return groups.flatMap((group) => (Array.isArray(group) ? group.filter((value): value is string => typeof value === "string") : []));
}

export function describeApiError(cause: unknown, fallback: string): string[] {
  if (cause instanceof ApiError) {
    const details = collectDetailMessages(cause.details);
    return details.length ? details : [cause.message];
  }
  return [cause instanceof Error && cause.message ? cause.message : fallback];
}

export function setSessionListener(listener: (token: string | null) => void): () => void {
  sessionListener = listener;
  return () => { if (sessionListener === listener) sessionListener = null; };
}

/** Whether a session token is held; guests browsing the catalogue have none. */
export function hasSession(): boolean {
  return accessToken !== null;
}

export function setAccessToken(token: string | null): void {
  if (accessToken === token) return;
  accessToken = token;
  sessionListener?.(token);
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  const payload = await response.json().catch(() => ({})) as {
    error?: { message?: string; code?: string; correlationId?: string; details?: unknown };
  };
  if (!response.ok) {
    const code = payload.error?.code;
    if (code === "MFA_REQUIRED" || code === "MFA_ENROLLMENT_REQUIRED") mfaListener?.(code);
    throw new ApiError(
      payload.error?.message ?? "No pudimos completar la solicitud.",
      payload.error?.code ?? `HTTP_${response.status}`,
      payload.error?.correlationId,
      payload.error?.details
    );
  }
  return payload as T;
}

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  const tokenAtStart = accessToken;
  refreshPromise = fetch("/api/v1/auth/refresh", {
    method: "POST",
    credentials: "include"
  })
    .then((response) => (response.status === 204
      ? null
      : parseResponse<{ data: { accessToken: string } }>(response).then((payload) => payload.data.accessToken)))
    .catch(() => null)
    .then((token) => {
      // A login or registration can land while this request is in flight. That session is
      // newer than this response, so an empty result must not be allowed to discard it.
      if (token === null && accessToken !== tokenAtStart) return accessToken;
      setAccessToken(token);
      return token;
    })
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit & { retryAuth?: boolean } = {}
): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  if (accessToken) headers.set("authorization", `Bearer ${accessToken}`);
  const response = await fetch(path, { ...options, headers, credentials: "include" });
  if (response.status === 401 && options.retryAuth !== false && !path.includes("/auth/")) {
    const token = await refreshAccessToken();
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
      return parseResponse<T>(await fetch(path, { ...options, headers, credentials: "include" }));
    }
  }
  return parseResponse<T>(response);
}

/** Fetches a binary resource (a PDF) with the session token; a 401 is retried once after a refresh. */
export async function apiBlob(path: string): Promise<Blob> {
  const attempt = async (token: string | null) => fetch(path, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
    credentials: "include"
  });
  let response = await attempt(accessToken);
  if (response.status === 401) {
    const token = await refreshAccessToken();
    if (token) response = await attempt(token);
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as { error?: { message?: string; code?: string; correlationId?: string } };
    throw new ApiError(payload.error?.message ?? "No pudimos abrir el documento.", payload.error?.code ?? `HTTP_${response.status}`, payload.error?.correlationId);
  }
  return response.blob();
}

export { refreshAccessToken };
